import {Component, Input, OnInit, DestroyRef} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RULE_OUTCOME, PLAN_VALIDATION_STAGE} from '../common/constants';
import {RuleResults, RuleResultDetails} from '../plan-detail/plan-detail.model';
import {NlrPlanDetailService} from '../plan-detail/plan-detail.service';
import {PlanModelService} from '../common/service/plan-model.service';
import {AlertModalService} from '../common/component/modal/alert-modal/alert-modal.service';
import {NlrpValidationResultService} from './validation-results.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {AppConfigService} from '../common/service/app-config.service';
import {NlrPaginationComponent} from '../plan-dashboard/inner/dashboard-result/nlr-pagination.component';
import {NlrTooltipDirective} from '../common/directive/tooltip/nlr-tooltip-directive';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'validation-results',
    imports: [CommonModule, FormsModule, NlrPaginationComponent, NlrTooltipDirective],
    templateUrl: './validation-results.component.html',
    styleUrl: './validation-results.component.scss'
})
export class ValidationResultsComponent implements OnInit {
    @Input() isAnalysisTabEnabled: boolean;
    @Input() isDesignTabEnabled: boolean;
    @Input() isInterestsTabEnabled: boolean;
    @Input() isCertificationTabEnabled: boolean;

    protected readonly RULE_OUTCOME = RULE_OUTCOME;
    protected readonly PLAN_VALIDATION_STAGE = PLAN_VALIDATION_STAGE;

    title = 'Validation Results';

    includePass: boolean = false;
    includeInfo: boolean = true;
    includeWarning: boolean = true;
    includeError: boolean = true;

    isExportReportBtnDisabled = undefined;

    selectedTabType = PLAN_VALIDATION_STAGE.LODGEMENT_INFORMATION;
    validationResults: RuleResults[];
    pagenatedResults: RuleResults[];

    pageSize = 10;
    start = 0;
    end = 0;
    total = 0;
    currentPage = 1;

    constructor(
        private readonly nlrPlanDetailService: NlrPlanDetailService,
        private readonly planModelService: PlanModelService,
        private readonly nlrValidationResultService: NlrpValidationResultService,
        private readonly alertModalService: AlertModalService,
        private readonly eventBus: NgEventBus,
        private readonly appConfigService: AppConfigService,
        private readonly destroyRef: DestroyRef
    ) {}

    ngOnInit(): void {
        this.nlrPlanDetailService.getValidationResults().subscribe(result => {
            this.validationResults = result;
            this.onValidationResultsChange();
        });

        this.selectedTabType = PLAN_VALIDATION_STAGE.LODGEMENT_INFORMATION;

        this.eventBus
            .on('nlr:selectValidationTab')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                this.selectedTabType = meta.data;
            });
    }
    paginatedValidationResultsData(page: number) {
        this.start = (page - 1) * this.pageSize;
        this.end = this.start + this.pageSize;
        this.pageChange(page);
        this.updatePaginationCounters(this.currentPage);
    }

    pageChange(page: number) {
        this.currentPage = page;
    }

    onChangeBusinessRuleFilter(event: Event) {
        const target = event.target as HTMLElement;
        switch (target.id) {
            case 'pass':
                this.includePass = !this.includePass;
                break;
            case 'info':
                this.includeInfo = !this.includeInfo;
                break;
            case 'warning':
                this.includeWarning = !this.includeWarning;
                break;
            case 'error':
                this.includeError = !this.includeError;
                break;
            default:
                return;
        }
        this.paginationCallback();
    }

    onValidationResultsChange() {
        this.currentPage = 1;
        this.paginationCallback();
    }

    paginationCallback() {
        this.updatePaginationCounters(1);
    }

    selectTab(tabEventType: string) {
        this.selectedTabType = tabEventType;
        this.updatePaginationCounters(this.currentPage);
    }

    updatePaginationCounters(newPageNumber: number) {
        let results = [];

        if (this.validationResults && this.validationResults.length > 0) {
            results = this.validationResults
                .filter(result => {
                    return this.applyFilter(result);
                })
                .filter(result => {
                    return this.includeByStage(result, this.selectedTabType, true);
                });

            this.total = results.length;
        } else {
            this.total = 0;
        }

        const lastPage = Math.max(1, Math.ceil(this.total / this.pageSize));
        newPageNumber = Math.min(Math.max(1, newPageNumber), lastPage);

        this.start = (newPageNumber - 1) * this.pageSize + 1;
        this.end = this.start - 1 + this.pageSize;
        if (this.end >= this.total) {
            this.end = this.total;
        }

        this.currentPage = newPageNumber;
        this.pagenatedResults = this.paginatedResult(this.currentPage, results);
    }

    paginatedResult(page: number, results: RuleResults[]): RuleResults[] {
        const startIndex = (page - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return results.slice(startIndex, endIndex);
    }

    getValidationResultIcon(outcome: string) {
        let iconClass = '';
        switch (outcome) {
            case this.RULE_OUTCOME.PASS:
                iconClass = 'nlrp-icon-verified-passed';
                break;
            case this.RULE_OUTCOME.INFO:
                iconClass = 'nlrp-icon-info info';
                break;
            case this.RULE_OUTCOME.WARNING:
                iconClass = 'nlrp-icon-warning warning';
                break;
            case this.RULE_OUTCOME.ERROR:
                iconClass = 'nlrp-icon-error error';
                break;
            case this.RULE_OUTCOME.CRITICAL:
                iconClass = 'nlrp-icon-error error';
                break;
            default:
                console.error('Unexpected result type: ');
                iconClass = '';
                break;
        }
        return iconClass;
    }

    includeByStage(result: RuleResults, stage: string, triggeredManually: boolean) {
        let stageToCheck = this.selectedTabType;
        if (typeof triggeredManually === 'boolean') {
            stageToCheck = stage;
        }
        return result.stage === stageToCheck;
    }

    isTabSelected(tabEventType: string) {
        return this.selectedTabType === tabEventType;
    }

    resultExists() {
        let found = false;

        if (this.validationResults && this.validationResults.length > 0) {
            for (let i = 0; i < this.validationResults.length; i++) {
                this.applyFilter(this.validationResults[i]);
                if (this.applyFilter(this.validationResults[i]) && this.includeByStage(this.validationResults[i], this.selectedTabType, true)) {
                    found = true;
                    break;
                }
            }
        }
        return found;
    }

    applyFilter(ruleResult) {
        return (
            (this.includePass && ruleResult.type === this.RULE_OUTCOME.PASS) ||
            (this.includeInfo && ruleResult.type === this.RULE_OUTCOME.INFO) ||
            (this.includeWarning && ruleResult.type === this.RULE_OUTCOME.WARNING) ||
            (this.includeError && (ruleResult.type === this.RULE_OUTCOME.ERROR || ruleResult.type === this.RULE_OUTCOME.CRITICAL))
        );
    }

    getTotalResultsByStage(stage: string) {
        const results = structuredClone(this.validationResults);

        let totals = 0;
        if (results.length > 0) {
            totals = results
                .filter(result => {
                    return this.includeByStage(result, stage, true);
                })
                .filter(result => {
                    return result.type == RULE_OUTCOME.ERROR || result.type == RULE_OUTCOME.CRITICAL;
                }).length;
        }

        return totals;
    }

    generateValidationsReport() {
        console.debug(this.title + ' controller generateValidationsReport');

        this.isExportReportBtnDisabled = true;

        const errorMessage = 'Error on generating validations report';
        const url = this.appConfigService.settings.apiGatewayUrl + '/validation/report/' + this.planModelService.getModel().id;

        //TO-DO: The below url is for test purpose, it needs to be removed once the backend is ready
        //const url = '/NTR.App.NLRPlan/wa/svc/validation/report/11011';
        const subs = this.nlrValidationResultService.getValidationsReport(url).subscribe({
            next: data => {
                console.debug('Success on generating validations report: ' + data);

                const file = new Blob([data], {type: 'application/pdf'});
                const fileURL = URL.createObjectURL(file);
                const params = ['height=' + screen.height, 'width=' + screen.width, 'fullscreen=yes', 'location=nos'].join(',');
                const popup = window.open(fileURL, '"Validation Report', params);

                if (popup == null) {
                    this.alertModalService.createAlertMessage(
                        'A window could not be opened due to a pop-up blocker. Please enable pop-ups in the browser and try again.',
                        'OK',
                        'Window blocked'
                    );
                    return;
                }
                popup.moveTo(0, 0);
            },
            error: () => {
                this.alertModalService.createAlertMessage(errorMessage);
                this.isExportReportBtnDisabled = false;
            },
            complete: () => {
                this.isExportReportBtnDisabled = false;
            }
        });

        this.eventBus.cast('nlr:busy', subs);
    }

    formatDetailsToolTip(ruleDetails: RuleResultDetails[]) {
        let result = '';
        ruleDetails.map((ruleDetail: RuleResultDetails, index: number) => {
            const ruleDetails = ruleDetail.outcome + ' - ' + ruleDetail.message;
            result = index === 0 ? result + ruleDetails : result + '\n' + ruleDetails;
        });
        return result;
    }
}
